import { css, Global } from '@emotion/react'
import { useContext, useMemo } from 'react'
import { FullScreenVideoContext } from '../../../../context/fullScreenVideoContext'
import { PageContext } from '../../../../context/PageContextProvider'
import { ViddyLPContext } from '../../../../context/viddyLPContext'
import { useBodyScrollLock } from '../../../../hooks/useBodyScrollLock'
import { lpGlobalCSS } from '../../../../style/lpGlobalStyles'
import {
    DESKTOP_FONT_SCALE,
    FONT_SCALE_VAR,
    ScrollingLayout,
} from '../../../../style/scrollingLayout'
import { BrandChat } from '../../../brand-chat/BrandChat'
import { hasBrandChatSection } from '../../../brand-chat/brandChatSections'
import { useBrandChatParam } from '../../../brand-chat/useBrandChatParam'
import { DialogPortalTarget, DialogTarget } from '../../../shared/Dialog'
import { CartDrawer } from '../../full-screen/Cart'
import { PLPDrawer } from '../../full-screen/PLPDrawer'
import { ProductDrawer } from '../../full-screen/ProductDrawer'
import { FullScreenVideoPlayer } from '../../full-screen/video-player/FullScreenVideoPlayer'
import { ScrollingContentBlock } from './ScrollingContentBlock'

/**
 * The scrolling storefront layout [sc-36106]: one document that scrolls.
 *
 * Nothing here declares `container-type`. A page-wide container would make
 * every container rule in the tree resolve against the page rather than the
 * element's own parent, and would trap `position: fixed` descendants against
 * the container box. Leaving it out means chrome can use ordinary media
 * queries and fixed positioning resolves against the viewport as authored.
 *
 * Sections not yet migrated to read their own container are still capped to
 * the legacy column width; they become container-aware one at a time.
 */
const ScrollingLP: React.FC = () => {
    const { isCartOpen, activeLPLink, isGridExpanded, lastOpenedOverlay } =
        useContext(PageContext)
    const { isFullScreenPlayerOpen } = useContext(FullScreenVideoContext)
    const { landingPage } = useContext(ViddyLPContext)
    const brandChatParam = useBrandChatParam()
    const showBrandChat =
        brandChatParam || hasBrandChatSection(landingPage.bodyContent)

    const pdpZIndex = lastOpenedOverlay === 'cart' ? 5 : 6
    const cartZIndex = lastOpenedOverlay === 'cart' ? 6 : 5

    // Set on the document rather than the shell because the overlays render
    // outside its subtree.
    const globalStyles = useMemo(() => {
        return css`
            ${lpGlobalCSS()}
            ${ScrollingLayout.DESKTOP_MEDIA} {
                :root {
                    ${FONT_SCALE_VAR}: ${DESKTOP_FONT_SCALE};
                }
            }
        `
    }, [])

    useBodyScrollLock(
        isCartOpen ||
            activeLPLink !== null ||
            isGridExpanded ||
            isFullScreenPlayerOpen,
    )

    return (
        <>
            <Global styles={globalStyles} />
            <DialogTarget>
                <BrandChat zIndex={4} enabled={showBrandChat}>
                    <ScrollingContentBlock />
                    <PLPDrawer zIndex={4} />
                </BrandChat>
                <ProductDrawer zIndex={pdpZIndex} />
                <CartDrawer zIndex={cartZIndex} />
                <FullScreenVideoPlayer zIndex={7} />
                <DialogPortalTarget zIndex={8} />
            </DialogTarget>
        </>
    )
}

export default ScrollingLP
